import { useCallback, useContext, useMemo } from 'react'
import { UserMeContext } from '../contexts/UserMeContext'
import { CompanyGridCols, CompaniesGridColumnSettingsName } from '@finance-lab/utils/types'

const settingName: CompaniesGridColumnSettingsName = 'default'

const defaultValues: Array<{
    name: CompanyGridCols
    visibleIn: boolean
    order: number
}> = [
    { name: 'country', visibleIn: true, order: 0 },
    { name: 'name', visibleIn: true, order: 10 },
    { name: 'sector', visibleIn: true, order: 20 },
    { name: 'marketList', visibleIn: true, order: 30 },
    { name: 'capSize', visibleIn: true, order: 40 },
    { name: 'latestIssue', visibleIn: true, order: 50 },
    { name: 'financialAdvisors', visibleIn: false, order: 60 },
    { name: 'legalAdvisors', visibleIn: false, order: 70 },
    { name: 'issuingAgents', visibleIn: false, order: 80 },
]

export const useCompanyGridColumnSettings = () => {
    const { isLoading: isLoadingUser, updateMe, user } = useContext(UserMeContext)

    const visibleColumns = useMemo(() => {
        const defaults = defaultValues.reduce(
            (acc, col) => ({
                ...acc,
                [col.name]: col.visibleIn,
            }),
            {} as Record<CompanyGridCols, boolean>,
        )

        return {
            ...defaults,
            ...user?.settings?.companiesGrid?.columnVisibility?.[settingName],
        }
    }, [user?.settings?.companiesGrid?.columnVisibility])

    const columnOrder = useMemo(() => {
        const order = user?.settings?.companiesGrid?.columnOrder?.[settingName]

        if (!order) {
            return defaultValues.sort((a, b) => a.order - b.order).map((v) => v.name)
        }

        return [...defaultValues].sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name)).map((v) => v.name)
    }, [user?.settings?.companiesGrid?.columnOrder])

    const handleChangeVisibleColumns = useCallback(
        (settings: Partial<Record<CompanyGridCols, boolean>>) => {
            updateMe({
                settings: {
                    companiesGrid: {
                        columnVisibility: { [settingName]: settings },
                    },
                },
            }).catch((err) => console.error(err))
        },
        [updateMe],
    )

    const handleChangeColumnOrder = useCallback(
        (settings: Array<CompanyGridCols>) => {
            updateMe({
                settings: {
                    companiesGrid: {
                        columnOrder: { [settingName]: settings },
                    },
                },
            }).catch((err) => console.error(err))
        },
        [updateMe],
    )

    const handleResetColumnsSettings = useCallback(() => {
        updateMe({
            settings: {
                companiesGrid: {
                    columnOrder: { [settingName]: null },
                    columnVisibility: { [settingName]: null },
                },
            },
        })
    }, [updateMe])

    return useMemo(
        () => ({
            availableColumns: defaultValues.map((col) => col.name),
            columnOrder,
            isLoading: isLoadingUser,
            onChangeColumnOrder: handleChangeColumnOrder,
            onChangeVisibleColumns: handleChangeVisibleColumns,
            onResetColumnsSettings: handleResetColumnsSettings,
            visibleColumns,
        }),
        [
            columnOrder,
            handleChangeColumnOrder,
            handleChangeVisibleColumns,
            handleResetColumnsSettings,
            isLoadingUser,
            visibleColumns,
        ],
    )
}
